// Servicio de Cloudinary - Manejo de subida y transformación de imágenes
// Proporciona funciones para subir imágenes y obtener URLs optimizadas
// Usa la API directa de Cloudinary (no requiere SDK de Node.js en el frontend)

// Configuración de Cloudinary desde variables de entorno
const sanitizeEnvValue = (value) => typeof value === 'string' ? value.trim().replace(/^['"]|['"]$/g, '').trim() : value
const cloudinaryConfig = {
  cloudName: sanitizeEnvValue(import.meta.env.VITE_CLOUDINARY_CLOUD_NAME),
  uploadPreset: sanitizeEnvValue(import.meta.env.VITE_CLOUDINARY_UPLOAD_PRESET)
}

/**
 * Subir una imagen a Cloudinary usando upload widget o API directa
 * @param {File} file - Archivo de imagen a subir
 * @param {string} folder - Carpeta donde guardar (opcional)
 * @param {Function} onProgress - Callback de progreso (opcional)
 * @returns {Promise<Object>} Objeto con la información de la imagen subida
 */
export const uploadImage = async (file, folder = 'products', onProgress = null) => {
  if (!cloudinaryConfig.cloudName || !cloudinaryConfig.uploadPreset) {
    throw new Error('Cloudinary no está configurado. Verifica las variables de entorno.')
  }

  try {
    if (onProgress) onProgress(10)

    // Crear FormData para subir a Cloudinary
    const formData = new FormData()
    formData.append('file', file)
    formData.append('upload_preset', cloudinaryConfig.uploadPreset)
    formData.append('folder', folder)
    formData.append('cloud_name', cloudinaryConfig.cloudName)

    // URL de upload de Cloudinary
    const uploadUrl = `https://api.cloudinary.com/v1_1/${cloudinaryConfig.cloudName}/image/upload`

    if (onProgress) onProgress(30)

    const response = await fetch(uploadUrl, {
      method: 'POST',
      body: formData
    })

    if (!response.ok) {
      const errorData = await response.json().catch(() => ({}))
      const errorMessage = errorData.error?.message || `Error al subir: ${response.status} ${response.statusText}`
      throw new Error(errorMessage)
    }

    if (onProgress) onProgress(80)

    const result = await response.json()

    if (onProgress) onProgress(100)

    // Generar URL de thumbnail
    const thumbnailUrl = result.secure_url.replace('/upload/', '/upload/w_300,h_300,c_fill/')

    // Retornar información estructurada compatible con el formato anterior
    return {
      fileId: result.public_id,
      url: result.secure_url,
      thumbnailUrl: thumbnailUrl,
      width: result.width,
      height: result.height,
      size: result.bytes,
      name: result.original_filename,
      format: result.format
    }
  } catch (error) {
    console.error('Error al subir imagen a Cloudinary:', error)
    throw new Error(`Error al subir imagen: ${error.message || 'Error desconocido'}`)
  }
}

/**
 * Obtener URL de imagen con transformaciones
 * @param {string} publicId - Public ID de la imagen en Cloudinary o URL completa
 * @param {Object} transformations - Transformaciones a aplicar { width, height, crop }
 * @returns {string} URL de la imagen transformada
 */
export const getImageUrl = (publicId, transformations = {}) => {
  // Si no hay publicId, retornar placeholder
  if (!publicId) {
    return 'data:image/svg+xml,%3Csvg xmlns="http://www.w3.org/2000/svg" viewBox="0 0 400 400"%3E%3Crect fill="%23f5f0eb" width="400" height="400"/%3E%3C/svg%3E'
  }

  // Si ya es una URL completa de Cloudinary
  if (publicId.startsWith('http') && publicId.includes('cloudinary.com')) {
    // Si no hay transformaciones solicitadas, retornar la URL directamente
    if (Object.keys(transformations).length === 0) {
      return publicId
    }
    
    // Si hay transformaciones solicitadas, extraer el public_id y reconstruir
    try {
      // Extraer el public_id de la URL
      // Formato: https://res.cloudinary.com/{cloud_name}/image/upload/{transformaciones}/{public_id}.{ext}
      // O: https://res.cloudinary.com/{cloud_name}/image/upload/v{version}/{public_id}.{ext}
      
      const urlMatch = publicId.match(/\/upload\/(.+)$/)
      if (!urlMatch) {
        return publicId
      }
      
      const afterUpload = urlMatch[1]
      const parts = afterUpload.split('/')
      
      // El public_id siempre es el último elemento (puede tener extensión)
      // Las transformaciones están antes del public_id
      let publicIdWithExt = parts[parts.length - 1]
      
      // Si el último elemento parece una transformación (contiene _ o ,), 
      // significa que la URL está mal formada, intentar extraer el public_id real
      if (publicIdWithExt.includes('_') && publicIdWithExt.includes(',')) {
        // Esto es una transformación, buscar el public_id real
        // El public_id real debería estar en el path, buscar un elemento que no sea transformación
        for (let i = parts.length - 1; i >= 0; i--) {
          const part = parts[i]
          // Si no es versión (v123) y no parece transformación (no tiene _ y , juntos)
          if (!/^v\d+$/.test(part) && !(part.includes('_') && part.includes(','))) {
            publicIdWithExt = part
            break
          }
        }
      }
      
      // Quitar extensión si existe
      const publicIdClean = publicIdWithExt.split('.')[0]
      
      // Construir nueva URL con las transformaciones solicitadas
      const transformationString = buildTransformationString(transformations)
      const baseUrl = `https://res.cloudinary.com/${cloudinaryConfig.cloudName}/image/upload`
      
      if (transformationString) {
        return `${baseUrl}/${transformationString}/${publicIdClean}`
      }
      return `${baseUrl}/${publicIdClean}`
    } catch (error) {
      console.error('Error procesando URL de Cloudinary:', error)
      // Si hay error, retornar la URL original
      return publicId
    }
  }

  // Si es una URL completa pero no es de Cloudinary, retornarla directamente
  if (publicId.startsWith('http')) {
    return publicId
  }

  // Si Cloudinary no está configurado, retornar placeholder
  if (!cloudinaryConfig.cloudName) {
    return 'data:image/svg+xml,%3Csvg xmlns="http://www.w3.org/2000/svg" viewBox="0 0 400 400"%3E%3Crect fill="%23f5f0eb" width="400" height="400"/%3E%3C/svg%3E'
  }

  // Construir URL desde public_id
  try {
    const transformationString = buildTransformationString(transformations)
    const baseUrl = `https://res.cloudinary.com/${cloudinaryConfig.cloudName}/image/upload`
    
    if (transformationString) {
      return `${baseUrl}/${transformationString}/${publicId}`
    }
    
    return `${baseUrl}/${publicId}`
  } catch (error) {
    console.error('Error generando URL de Cloudinary:', error)
    return 'data:image/svg+xml,%3Csvg xmlns="http://www.w3.org/2000/svg" viewBox="0 0 400 400"%3E%3Crect fill="%23f5f0eb" width="400" height="400"/%3E%3C/svg%3E'
  }
}

/**
 * Construir string de transformaciones para Cloudinary
 * @param {Object} transformations - Objeto con transformaciones
 * @returns {string} String de transformaciones en formato Cloudinary
 */
function buildTransformationString(transformations) {
  const parts = []
  
  if (transformations.width) {
    parts.push(`w_${transformations.width}`)
  }
  
  if (transformations.height) {
    parts.push(`h_${transformations.height}`)
  }
  
  if (transformations.crop) {
    parts.push(`c_${transformations.crop}`)
  } else if (transformations.width || transformations.height) {
    // Si hay width o height pero no crop, usar 'fill' por defecto
    parts.push('c_fill')
  }
  
  return parts.join(',')
}

/**
 * Eliminar una imagen de Cloudinary
 * NOTA: La eliminación requiere autenticación del servidor
 * Esta función está preparada para cuando implementes un endpoint de backend
 * @param {string} publicId - Public ID de la imagen a eliminar
 * @returns {Promise<boolean>} true si se eliminó correctamente
 */
export const deleteImage = async (publicId) => {
  // Por ahora, solo loguear que se intentó eliminar
  // En el futuro, puedes implementar un endpoint de backend para esto
  console.warn('Eliminación de imágenes requiere un endpoint de backend. Public ID:', publicId)
  return false
}

export default {
  uploadImage,
  getImageUrl,
  deleteImage
}

